import React, { useState } from 'react'
import { Flex, Grid, Text } from '@chilipiper/design-system'
import { SvgArrowLeft, SvgArrowRight } from '@chilipiper/icons/src/design-system'
import { Button } from '@chilipiper/design-system/src/new'
import { SvgZoom, SvgLocation } from '@chilipiper/icons/src/design-system/3rd-party-integrations'
import { OnboardingChiliPiperLogo, Progress } from '@chilipiper/onboarding'
import {
  useConnectZoomIntegration,
  useGetZoomIntegrationStatus,
} from '@chilipiper/meeting-location-integrations'
import { useCalendarIntegration } from '@chilipiper/integrations'
import { useFireSessionContext } from '@chilipiper/fire-session'
import { routes } from '../../../routes/routes'
import { Link } from '../../../components/link/Link'
import { Spacer } from '../components/Spacer'
import { useIsDesktop } from '../useIsDesktop'
import { LocationCard } from './location-card/LocationCard'

import { getCalendarLocationInfo } from './helpers'
import { getExternalCalendar } from '@/components/personal-settings/integrations/CalendarIntegration/helpers'

export const Location = () => {
  const isDesktop = useIsDesktop()

  const { data: zoomIntegration } = useGetZoomIntegrationStatus()
  const { mutate: handleZoomConnectClick } = useConnectZoomIntegration(window.location.href)
  const { userId } = useFireSessionContext()

  const { data: calendarIntegration } = useCalendarIntegration(userId)
  const userCalendarIntegration = calendarIntegration?.connectionStatuses[userId]

  const calendar = getCalendarLocationInfo(getExternalCalendar(userCalendarIntegration?.primary))

  const [selectedLocation, setSelectedLocation] = useState(calendar.name)

  return (
    <Flex
      alignItems='center'
      w='100%'
      h='100%'
      flexDirection='column'
      bg='bg/minor'
      overflow='auto'
      px={6}
      py={5}
      position='relative'
    >
      <OnboardingChiliPiperLogo />
      <Spacer />
      <Flex flexDirection='column' alignItems='center' gap={4} mb={7}>
        <Text fontSize={[20, 20, 32]} fontWeight={600} color='fg/major' textAlign='center'>
          Choose a meeting location
        </Text>
        <Text
          textStyle={isDesktop ? 'body-monoline' : 'body-multiline-tight'}
          color='fg/moderate'
          textAlign='center'
        >
          This will be the default location of every scheduled meeting.
        </Text>
      </Flex>

      <Flex flexDirection='column' maxWidth='528px' w='100%'>
        <Grid gridTemplateColumns={['1fr 1fr', '1fr 1fr', '1fr 1fr 1fr']} columnGap={4} rowGap={5}>
          <LocationCard
            icon={calendar.icon}
            name={calendar.name}
            showConnect={false}
            active={selectedLocation === calendar.name}
            onClick={() => setSelectedLocation(calendar.name)}
          />
          <LocationCard
            icon={SvgZoom}
            name={'Zoom'}
            showConnect={!zoomIntegration}
            onConnect={handleZoomConnectClick}
            active={selectedLocation === 'Zoom'}
            onClick={() => setSelectedLocation('Zoom')}
          />
          <LocationCard
            icon={SvgLocation}
            name={'In-Person Meeting'}
            showConnect={false}
            active={selectedLocation === 'In-Person Meeting'}
            onClick={() => setSelectedLocation('In-Person Meeting')}
          />
        </Grid>

        <Flex justifyContent='space-between' mt={7} mb={9}>
          <Link to={routes.onboarding.workingHours}>
            {linkProps => (
              <Button
                variant='minor'
                startContent={<Button.StartContent variant='icon' icon={SvgArrowLeft} />}
                as='a'
                {...linkProps}
              >
                Back
              </Button>
            )}
          </Link>
          <Link to={routes.onboarding.extension}>
            {linkProps => (
              <Button
                variant='major'
                endContent={<Button.EndContent variant='icon' icon={SvgArrowRight} />}
                as='a'
                data-test-id='location-next-button'
                {...linkProps}
              >
                Next
              </Button>
            )}
          </Link>
        </Flex>
        <Progress totalSteps={5} currentStep={4} />
      </Flex>
      <Spacer />
    </Flex>
  )
}
